import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#0A0A0B] card-line px-4 py-3.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#C9A227]/60 transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", volume: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. A desk officer will respond within one business day.");
      setForm({ name: "", email: "", company: "", volume: "", interest: "", message: "" });
    } catch {
      toast.error("Submission failed. Please try again or contact the desk directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-36 bg-[#0A0A0B] grain relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-16">
        <Reveal className="lg:col-span-2">
          <Eyebrow>Private Client Desk</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug">
            Begin with a <span className="gold-text">conversation.</span>
          </h2>
          <p className="mt-6 text-base text-zinc-400 leading-relaxed">
            Allocations are extended to verified counterparties only. Submit an
            enquiry and a desk officer will initiate the onboarding protocol.
          </p>
          <img src="/assets/logo-mark.webp" alt="Zorem Commodities gold emblem" width="176" height="264" className="mt-12 w-44 opacity-90" loading="lazy" />
          <div className="mt-10 space-y-3 font-mono2 text-[12px] tracking-[0.15em] text-zinc-400 uppercase">
            <div data-testid="contact-office-hq">HQ · 300 W. Clarendon Ave, Suite 325, Phoenix, AZ</div>
            <div data-testid="contact-office-corridors">Corridors · Rwanda · DRC · West Africa</div>
            <div data-testid="contact-office-fulfillment">Fulfillment · Dubai · London · Zurich · Hong Kong</div>
            <div data-testid="contact-email" className="pt-2 text-[#C9A227]">b@zoremcommodities.com · +1 438 886 1245</div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="card-line bg-[#121215] p-8 lg:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required aria-label="Full name" data-testid="contact-input-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
              <input required type="email" aria-label="Institutional email" data-testid="contact-input-email" placeholder="Institutional email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input aria-label="Company or entity" data-testid="contact-input-company" placeholder="Company / Entity" value={form.company} onChange={set("company")} className={inputCls} />
              <select required aria-label="Indicative volume" data-testid="contact-select-volume" value={form.volume} onChange={set("volume")} className={inputCls}>
                <option value="" disabled>Indicative volume</option>
                <option value="1-10kg">1 – 10 kg Au equivalent</option>
                <option value="10-100kg">10 – 100 kg Au equivalent</option>
                <option value="100kg+">100 kg+ Au equivalent</option>
                <option value="mandate">Structured mandate</option>
              </select>
            </div>
            <select required aria-label="Desk of interest" data-testid="contact-select-interest" value={form.interest} onChange={set("interest")} className={inputCls}>
              <option value="" disabled>Desk of interest</option>
              <option value="gold">Physical Gold</option>
              <option value="silver">Silver Bullion</option>
              <option value="pgm">Platinum Group Metals</option>
              <option value="concentrates">Mining Supply & Concentrates</option>
            </select>
            <textarea
              data-testid="contact-input-message"
              rows={4}
              placeholder="Nature of enquiry"
              value={form.message}
              onChange={set("message")}
              className={inputCls}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full sm:w-auto px-10 py-4 text-[12px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-all disabled:opacity-60"
            >
              {sending ? "Transmitting…" : "Submit Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
